import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Loader2, Ticket, AlertCircle, ArrowLeft, CheckCircle2, Clock, Info, Phone } from "lucide-react";
import { ParallaxBackground } from "@/components/site/ParallaxBackground";
import { Header } from "@/components/site/Header";
import { formatApiError, labelFor } from "@/lib/errors";
import { useRegPrices } from "@/lib/useRegPrices";

const API = process.env.REACT_APP_BACKEND_URL;
const SALE_OPEN_AT = new Date("2026-09-22T00:00:00+08:00").getTime();

const PACKAGE_INCLUDES = [
  "1x Seminar Ticket",
  "1x Championship Ticket",
  "1x Event Jersey",
  "1x Participation Certificate",
  "1x Lucky Draw Coupon",
];

const NOTICES = [
  "This package is strictly for participants aged 12 years old and above.",
  "The seminar ticket grants the participant access to the seminar and participation in the available seminar activities, including training sessions, activities, and photo opportunities with Muay Thai superstars.",
  "The Championship Ticket is for the registered participant only and does not include admission for any accompanying person.",
  "The organizer reserves the right to deny entry or cancel redemption if false or inaccurate information is provided. No refunds will be issued in such cases.",
];

const REDEMPTION_SCHEDULE = [
  ["24 November 2026 (Tuesday)", "10:00 AM – 8:00 PM"],
  ["25 November 2026 (Wednesday)", "10:00 AM – 8:00 PM"],
  ["26 November 2026 (Thursday)", "10:00 AM – 11:00 PM"],
  ["27 November 2026 (Friday)", "10:00 AM – 11:00 PM"],
  ["28 November 2026 (Saturday)", "7:00 AM – 10:00 PM"],
];

const GENDERS = ["Male", "Female"];
const JERSEY_SIZES = ["XS", "S", "M", "L", "XL", "2XL", "3XL"];

const inputCls =
  "w-full rounded-xl border border-white/15 bg-void/60 px-4 py-3 font-body text-sm text-white outline-none placeholder:text-slate-500 focus:border-neon-cyan transition-colors";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono-cap mb-2 block text-[10px] text-neon-cyan">{label}</span>
    {children}
  </label>
);

const Select = ({ value, onChange, options, placeholder, testid }) => (
  <select
    value={value}
    onChange={onChange}
    className={`${inputCls} appearance-none ${value ? "text-white" : "text-slate-500"}`}
    data-testid={testid}
  >
    <option value="" disabled>{placeholder}</option>
    {options.map((o) => (
      <option key={o} value={o} className="bg-void text-white">{o}</option>
    ))}
  </select>
);

const INITIAL = { full_name: "", nric_passport: "", gender: "", contact_number: "", jersey_size: "", email: "" };

export default function SeminarAdultPage() {
  const [form, setForm] = useState(INITIAL);
  const [error, setError] = useState("");
  const [resume, setResume] = useState(null);
  const prices = useRegPrices();

  useEffect(() => {
    if (error || resume) (document.querySelector('[data-testid="sem-error"]') || document.querySelector('[data-testid="resume-payment"]'))?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [error, resume]);
  const [submitting, setSubmitting] = useState(false);
  const saleOpen = Date.now() >= SALE_OPEN_AT;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setResume(null);
    const missing = Object.keys(form).filter((k) => !String(form[k]).trim());
    if (missing.length) {
      setError("Please complete: " + missing.map(labelFor).join(" · "));
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/registrations/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reg_type: "seminar_adult", ...form }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 409 && data.detail?.resume) {
          setResume(data.detail);
          setSubmitting(false);
          return;
        }
        throw new Error(formatApiError(data.detail));
      }
      localStorage.setItem("pahlawan_last_registration", data.registration_id);
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen text-white">
      <ParallaxBackground />
      <Header />
      <main className="mx-auto max-w-3xl px-4 pb-24 pt-28 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <Link to="/register" className="mb-5 inline-flex items-center gap-2 font-mono-cap text-[10px] text-slate-400 transition-colors hover:text-neon-cyan" data-testid="reg-back-link">
            <ArrowLeft className="h-3.5 w-3.5" /> All Registrations
          </Link>
          <p className="font-mono-cap mb-3 text-[11px] text-neon-cyan">WPC 3.0 · Stadium Titiwangsa, Kuala Lumpur</p>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h1 className="font-display text-4xl leading-[0.95] sm:text-5xl">
              SEMINAR MUAY THAI <span className="bg-gradient-to-r from-violet-400 via-cyan-300 to-green-400 bg-clip-text text-transparent">(ADULT)</span>
            </h1>
            <div className="shrink-0 rounded-2xl border border-neon-pink/40 bg-neon-pink/10 px-5 py-3 text-right">
              <p className="font-mono-cap text-[9px] text-slate-400">Normal Price <span className="line-through">RM199.00</span></p>
              <p className="font-display text-2xl gradient-text" data-testid="reg-price">RM{prices.seminar_adult}</p>
            </div>
          </div>
          <p className="mt-3 font-body text-sm text-slate-400">For participants aged 12 and above. Train with Muay Thai superstars. <span className="text-slate-200">Seminar: 28 November 2026 (Saturday), 8:00 – 10:30 AM, Stadium Titiwangsa.</span></p>


          <div className="mt-6 rounded-3xl border border-white/10 bg-surface/80 p-6" data-testid="seminar-package">
            <p className="font-mono-cap mb-3 text-[10px] text-neon-pink">Package Includes</p>
            <ul className="space-y-2.5">
              {PACKAGE_INCLUDES.map((item) => (
                <li key={item} className="flex items-center gap-2.5 font-body text-sm text-slate-200">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-neon-cyan" /> {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-4 rounded-3xl border border-white/10 bg-surface/80 p-6">
            <p className="font-mono-cap mb-3 flex items-center gap-2 text-[10px] text-neon-pink"><Info className="h-4 w-4" /> Important Notice</p>
            <ul className="space-y-2">
              {NOTICES.map((n) => (
                <li key={n} className="font-body text-xs leading-relaxed text-slate-400">* {n}</li>
              ))}
            </ul>
          </div>

          <div className="mt-4 rounded-3xl border border-neon-cyan/25 bg-neon-cyan/5 p-6" data-testid="seminar-redemption">
            <p className="font-mono-cap mb-3 flex items-center gap-2 text-[10px] text-neon-cyan"><Clock className="h-4 w-4" /> Ticket Redemption Counter Schedule</p>
            <div className="space-y-1.5">
              {REDEMPTION_SCHEDULE.map(([day, time]) => (
                <div key={day} className="flex items-center justify-between gap-4 font-body text-xs">
                  <span className="text-slate-300">{day}</span>
                  <span className="font-bold text-white">{time}</span>
                </div>
              ))}
            </div>
            <p className="mt-3 font-body text-[11px] text-slate-500">
              After a successful payment, you will receive a confirmation email with your registration number. Show that email at the redemption counter — registered participants must attend for verification.
            </p>
          </div>

          <div className="mt-4 flex items-center gap-3 rounded-2xl border border-neon-cyan/40 bg-neon-cyan/10 px-5 py-4 shadow-[0_0_30px_rgba(0,247,255,0.10)]" data-testid="reg-contact">
            <Phone className="h-5 w-5 shrink-0 text-neon-cyan" />
            <p className="font-body text-base text-slate-200">
              Any questions: <span className="font-bold text-neon-cyan">Admin Pahlawan 013-332 3998</span>
            </p>
          </div>
        </motion.div>

        <form onSubmit={submit} className="mt-10 space-y-6" data-testid="seminar-form">
          <div className="rounded-3xl border border-white/10 bg-surface/80 p-6 sm:p-8">
            <div className="mb-5 flex items-baseline gap-3">
              <span className="font-display text-2xl text-neon-pink/60">01</span>
              <h2 className="font-display text-xl text-white">PARTICIPANT DETAILS</h2>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Field label="Full Name (as per NRIC/Passport)">
                  <input value={form.full_name} onChange={set("full_name")} className={inputCls} placeholder="e.g. Muhammad Ali bin Ahmad" data-testid="sem-full-name" />
                </Field>
              </div>
              <Field label="NRIC / Passport Number">
                <input value={form.nric_passport} onChange={set("nric_passport")} className={inputCls} placeholder="e.g. 900101-14-5678" data-testid="sem-nric" />
              </Field>
              <div>
                <span className="font-mono-cap mb-2 block text-[10px] text-neon-cyan">Gender</span>
                <Select value={form.gender} onChange={set("gender")} options={GENDERS} placeholder="Gender — select" testid="sem-gender" />
              </div>
              <Field label="Contact Number">
                <input value={form.contact_number} onChange={set("contact_number")} className={inputCls} placeholder="e.g. 012-3456789" data-testid="sem-contact" />
              </Field>
              <div>
                <span className="font-mono-cap mb-2 block text-[10px] text-neon-cyan">Jersey Size</span>
                <Select value={form.jersey_size} onChange={set("jersey_size")} options={JERSEY_SIZES} placeholder="Size — select" testid="sem-jersey" />
              </div>
              <div className="sm:col-span-2">
                <Field label="Email (payment receipt & confirmation)">
                  <input type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" data-testid="sem-email" />
                </Field>
              </div>
            </div>
          </div>

          {!saleOpen && (
            <p className="flex items-center gap-2 rounded-2xl border border-amber-400/40 bg-amber-400/10 px-5 py-3 font-body text-sm text-amber-300" data-testid="seminar-sale-gate">
              <Clock className="h-4 w-4 shrink-0" /> Sales open on 22 September 2026 — mark your calendar!
            </p>
          )}

          {resume && (
            <div className="rounded-2xl border border-amber-400/40 bg-amber-400/10 px-5 py-4" data-testid="resume-payment">
              <p className="font-mono-cap text-[10px] tracking-wider text-amber-300">Registration already started</p>
              <p className="mt-1 font-body text-xs leading-relaxed text-slate-300">
                A registration with these details is already pending payment{resume.reg_no ? ` (${resume.reg_no})` : ""}.
                No need to fill the form again — just continue your payment.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                {resume.checkout_url ? (
                  <button
                    type="button"
                    onClick={() => {
                      localStorage.setItem("pahlawan_last_registration", resume.registration_id);
                      window.location.href = resume.checkout_url;
                    }}
                    className="inline-flex items-center gap-2 rounded-full bg-neon-pink px-5 py-2 font-mono-cap text-[10px] text-white transition-colors hover:bg-neon-cyan hover:text-void"
                    data-testid="resume-continue-btn"
                  >
                    Continue Payment
                  </button>
                ) : (
                  <p className="font-body text-[11px] text-slate-400">
                    Payment link unavailable — try again in about an hour, or WhatsApp Admin Pahlawan 013-332 3998.
                  </p>
                )}
                <button type="button" onClick={() => setResume(null)} className="font-mono-cap text-[10px] text-slate-400 underline underline-offset-2" data-testid="resume-dismiss">
                  Back to form
                </button>
              </div>
            </div>
          )}
          {error && (
            <p className="flex items-center gap-2 rounded-2xl border border-neon-pink/40 bg-neon-pink/10 px-5 py-3 font-body text-sm text-neon-pink" data-testid="sem-error">
              <AlertCircle className="h-4 w-4 shrink-0" /> {error}
            </p>
          )}

          <motion.button
            whileHover={saleOpen ? { scale: 1.01 } : undefined}
            whileTap={saleOpen ? { scale: 0.98 } : undefined}
            disabled={submitting || !saleOpen}
            className="flex w-full items-center justify-center gap-3 rounded-full bg-gradient-to-r from-neon-pink to-neon-violet py-5 font-mono-cap text-[12px] text-white disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="sem-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ticket className="h-4 w-4" />}
            {saleOpen ? (submitting ? "Preparing Secure Checkout…" : `Proceed To Payment — RM${prices.seminar_adult}`) : "Opens 22 September 2026"}
          </motion.button>
          <p className="text-center font-mono-cap text-[9px] text-slate-500">Secure payment via HitPay · FPX, cards & e-wallets accepted</p>
        </form>
      </main>
    </div>
  );
}
